import React, { useState, useRef } from 'react';
import JSZip from 'jszip';
import './ImageTransformer.css';

const ImageTransformer = () => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [converting, setConverting] = useState(false);
  const [creatingZip, setCreatingZip] = useState(false);
  const [progress, setProgress] = useState({});
  
  // Transform settings
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [selectedName, setSelectedName] = useState('transformed');
  const [format, setFormat] = useState('jpeg'); // jpeg, png, webp
  const [quality, setQuality] = useState(0.8);
  const [resizeMode, setResizeMode] = useState('fit'); // fit, fill, stretch
  
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle dropped files
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const imageFiles = droppedFiles.filter(file => 
      file.type.startsWith('image/')
    );
    
    addFiles(imageFiles);
  };

  // Handle file input change
  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  // Add files to state
  const addFiles = (newFiles) => {
    const filesWithId = newFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      status: 'pending', // pending, converting, completed, error
      transformedBlob: null,
      transformedSize: null,
      originalDimensions: null,
      finalDimensions: null
    }));
    
    setFiles(prev => [...prev, ...filesWithId]);
    
    // Get original dimensions for each file
    newFiles.forEach((file, index) => {
      getImageDimensions(file).then(dimensions => {
        const fileId = Date.now() + index;
        setFiles(prev => prev.map(f =>
          f.id === fileId ? { ...f, originalDimensions: dimensions } : f
        ));
      });
    });
  };

  // Get image dimensions
  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Transform image
  const transformImage = (file, index) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Set canvas to target dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Apply image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        let drawWidth, drawHeight, drawX, drawY;
        
        if (resizeMode === 'stretch') {
          // Stretch to exact dimensions
          drawWidth = width;
          drawHeight = height;
          drawX = 0;
          drawY = 0;
        } else {
          const originalRatio = img.width / img.height;
          const targetRatio = width / height;
          
          if (resizeMode === 'fit') {
            // Fit inside (letterbox/pillarbox)
            if (originalRatio > targetRatio) {
              drawWidth = width;
              drawHeight = width / originalRatio;
              drawX = 0;
              drawY = (height - drawHeight) / 2;
            } else {
              drawWidth = height * originalRatio;
              drawHeight = height;
              drawX = (width - drawWidth) / 2;
              drawY = 0;
            }
          } else if (resizeMode === 'fill') {
            // Fill entire area (crop if needed)
            if (originalRatio > targetRatio) {
              drawWidth = height * originalRatio;
              drawHeight = height;
              drawX = (width - drawWidth) / 2;
              drawY = 0;
            } else {
              drawWidth = width;
              drawHeight = width / originalRatio;
              drawX = 0;
              drawY = (height - drawHeight) / 2;
            }
          }
        }
        
        // Fill background with white for transparency
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);
        
        // Draw image with calculated dimensions
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

        const mimeType = format === 'jpeg' ? 'image/jpeg' : 
                        format === 'png' ? 'image/png' : 'image/webp';

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({
                blob,
                dimensions: { width, height } // Always return target dimensions
              });
            } else {
              reject(new Error('Failed to transform image'));
            }
          },
          mimeType,
          format === 'jpeg' ? quality : (format === 'webp' ? quality : undefined)
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = URL.createObjectURL(file.file);
    });
  };

  // Transform all files
  const transformAllFiles = async () => {
    setConverting(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.status !== 'pending') continue;

      try {
        // Update status to converting
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'converting' } : f
        ));

        setProgress(prev => ({ ...prev, [file.id]: 0 }));

        // Simulate progress
        const progressInterval = setInterval(() => {
          setProgress(prev => ({
            ...prev,
            [file.id]: Math.min((prev[file.id] || 0) + 15, 90)
          }));
        }, 100);

        // Transform image
        const result = await transformImage(file, i);
        
        clearInterval(progressInterval);
        setProgress(prev => ({ ...prev, [file.id]: 100 }));

        // Update file with transformed data
        setFiles(prev => prev.map(f =>
          f.id === file.id
            ? {
                ...f,
                status: 'completed',
                transformedBlob: result.blob,
                transformedSize: result.blob.size,
                finalDimensions: result.dimensions
              }
            : f
        ));

      } catch (error) {
        console.error('Transformation failed:', error);
        setFiles(prev => prev.map(f =>
          f.id === file.id ? { ...f, status: 'error' } : f
        ));
      }
    }

    setConverting(false);
  };

  // Download all transformed files as ZIP
  const downloadAsZip = async () => {
    const completedFiles = files.filter(f => f.status === 'completed');
    if (completedFiles.length === 0) return;

    setCreatingZip(true);
    const zip = new JSZip();
    
    // Add each transformed file to the ZIP
    completedFiles.forEach((file, index) => {
      const fileName = `${selectedName}${index + 1}.${format}`;
      zip.file(fileName, file.transformedBlob);
    });

    try {
      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedName}-images-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating ZIP file:', error);
    } finally {
      setCreatingZip(false);
    }
  };

  // Download single file
  const downloadFile = (file, index) => {
    if (!file.transformedBlob) return;

    const url = URL.createObjectURL(file.transformedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedName}${index + 1}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Remove file from list
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  // Clear all files
  const clearAll = () => {
    setFiles([]);
    setProgress({});
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const completedFiles = files.filter(f => f.status === 'completed');
  const pendingFiles = files.filter(f => f.status === 'pending');
  const totalTransformedSize = completedFiles.reduce((total, file) => total + (file.transformedSize || 0), 0);

  return (
    <div className="image-transformer">
      <div className="transformer-header">
        <h1>Image Transformer</h1>
        <p>Transform your images to specific dimensions, format, and naming convention</p>
      </div>

      {/* Drop Zone */}
      <div
        className={`drop-zone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="drop-zone-content">
          <div className="drop-icon">🖼️</div>
          <p>Drop your images here or <span className="click-text">click to browse</span></p>
          <p className="file-types">Supports: All image formats (JPG, PNG, WebP, GIF, etc.)</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </div>

      {/* Transform Settings */}
      <div className="transform-settings">
        <h3>Transform Settings</h3>
        
        <div className="settings-grid">
          <div className="setting-group">
            <label>Output Name Pattern</label>
            <input
              type="text"
              value={selectedName}
              onChange={(e) => setSelectedName(e.target.value)}
              placeholder="e.g., transformed"
              className="setting-input"
            />
            <small>Files will be named: {selectedName}1.{format}, {selectedName}2.{format}, etc.</small>
          </div>

          <div className="setting-group">
            <label>Output Format</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="setting-select"
            >
              <option value="jpeg">JPEG</option>
              <option value="png">PNG</option>
              <option value="webp">WebP</option>
            </select>
          </div>

          <div className="setting-group">
            <label>Width (px)</label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value) || 800)}
              min="1"
              max="5000"
              className="setting-input"
            />
          </div>

          <div className="setting-group">
            <label>Height (px)</label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value) || 600)}
              min="1"
              max="5000"
              className="setting-input"
            />
          </div>

          {(format === 'jpeg' || format === 'webp') && (
            <div className="setting-group">
              <label>Quality: {Math.round(quality * 100)}%</label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={quality}
                onChange={(e) => setQuality(parseFloat(e.target.value))}
                className="quality-slider"
              />
            </div>
          )}

          <div className="setting-group full-width">
            <label>Resize Mode</label>
            <div className="resize-mode-options">
              <label className="resize-option">
                <input
                  type="radio"
                  name="resizeMode"
                  value="fit"
                  checked={resizeMode === 'fit'}
                  onChange={(e) => setResizeMode(e.target.value)}
                />
                <span>Fit (preserve aspect ratio, may be smaller)</span>
              </label>
              <label className="resize-option">
                <input
                  type="radio"
                  name="resizeMode"
                  value="fill"
                  checked={resizeMode === 'fill'}
                  onChange={(e) => setResizeMode(e.target.value)}
                />
                <span>Fill (preserve aspect ratio, may crop)</span>
              </label>
              <label className="resize-option">
                <input
                  type="radio"
                  name="resizeMode"
                  value="stretch"
                  checked={resizeMode === 'stretch'}
                  onChange={(e) => setResizeMode(e.target.value)}
                />
                <span>Stretch (exact dimensions, may distort)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="dimension-preview">
          <strong>Output behavior: </strong>
          {resizeMode === 'fit' 
            ? `Images will be scaled to fit within ${width}×${height}px while preserving their original proportions (may be smaller than target size)` 
            : resizeMode === 'fill'
            ? `Images will be scaled to fill ${width}×${height}px while preserving their proportions (may crop parts of the image)`
            : `Images will be stretched/squeezed to exactly ${width}×${height}px (may distort the image)`
          }
        </div>
      </div>

      {/* Controls */}
      {files.length > 0 && (
        <div className="controls">
          <button
            className="btn btn-primary"
            onClick={transformAllFiles}
            disabled={converting || pendingFiles.length === 0}
          >
            {converting ? 'Transforming...' : `Transform ${pendingFiles.length} Images`}
          </button>
          
          {completedFiles.length > 0 && (
            <button
              className="btn btn-success"
              onClick={downloadAsZip}
              disabled={creatingZip}
            >
              {creatingZip 
                ? 'Creating ZIP...' 
                : `Download as ZIP (${completedFiles.length} files, ${formatFileSize(totalTransformedSize)})`
              }
            </button>
          )}
          
          <button
            className="btn btn-secondary"
            onClick={clearAll}
            disabled={converting || creatingZip}
          >
            Clear All
          </button>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="file-list">
          <h3>Files ({files.length})</h3>
          {files.map((file, index) => (
            <div key={file.id} className={`file-item ${file.status}`}>
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-details">
                  <span className="file-size">Original: {formatFileSize(file.size)}</span>
                  {file.originalDimensions && (
                    <span className="dimensions">
                      {file.originalDimensions.width}×{file.originalDimensions.height}px
                    </span>
                  )}
                  {file.transformedSize && (
                    <span className="transformed-size">
                      Transformed: {formatFileSize(file.transformedSize)}
                      {file.finalDimensions && (
                        <span className="final-dimensions">
                          ({file.finalDimensions.width}×{file.finalDimensions.height}px)
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div className="output-name">
                  Output: {selectedName}{index + 1}.{format}
                </div>
              </div>

              <div className="file-status">
                {file.status === 'pending' && <span className="status-badge pending">Pending</span>}
                {file.status === 'converting' && (
                  <div className="converting">
                    <span className="status-badge converting">
                      Transforming... ({width}×{height}px)
                    </span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress[file.id] || 0}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                {file.status === 'completed' && (
                  <div className="completed">
                    <span className="status-badge completed">✓ Completed</span>
                    <button
                      className="btn btn-small btn-download"
                      onClick={() => downloadFile(file, index)}
                    >
                      Download
                    </button>
                  </div>
                )}
                {file.status === 'error' && <span className="status-badge error">Error</span>}
              </div>

              <button
                className="remove-btn"
                onClick={() => removeFile(file.id)}
                disabled={converting}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageTransformer;
